import type { MeDto, MutationEnvelope } from "@home/shared";
import type { ListsState } from "@home/shared";
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

/** Локальный кэш: последнее известное состояние сервера и очередь неотправленных мутаций. */
export interface CachedState {
  server: ListsState;
  cursor: string;
  me: MeDto | null;
}

export interface SyncStorage {
  loadState(): Promise<CachedState | null>;
  saveState(state: CachedState): Promise<void>;
  loadOutbox(): Promise<MutationEnvelope[]>;
  enqueue(env: MutationEnvelope): Promise<void>;
  remove(mutationIds: string[]): Promise<void>;
  clear(): Promise<void>;
}

interface Schema extends DBSchema {
  kv: { key: string; value: CachedState };
  outbox: { key: number; value: { seq?: number; env: MutationEnvelope }; indexes: { mutationId: string } };
}

export class IdbSyncStorage implements SyncStorage {
  private db: Promise<IDBPDatabase<Schema>>;

  constructor(name = "home-lists") {
    this.db = openDB<Schema>(name, 1, {
      upgrade(db) {
        db.createObjectStore("kv");
        const outbox = db.createObjectStore("outbox", { keyPath: "seq", autoIncrement: true });
        outbox.createIndex("mutationId", "env.mutationId", { unique: true });
      },
    });
  }

  async loadState(): Promise<CachedState | null> {
    return (await (await this.db).get("kv", "state")) ?? null;
  }

  async saveState(state: CachedState): Promise<void> {
    await (await this.db).put("kv", state, "state");
  }

  async loadOutbox(): Promise<MutationEnvelope[]> {
    return (await (await this.db).getAll("outbox")).map((r) => r.env);
  }

  async enqueue(env: MutationEnvelope): Promise<void> {
    await (await this.db).add("outbox", { env });
  }

  async remove(mutationIds: string[]): Promise<void> {
    const tx = (await this.db).transaction("outbox", "readwrite");
    for (const id of mutationIds) {
      const key = await tx.store.index("mutationId").getKey(id);
      if (key !== undefined) await tx.store.delete(key);
    }
    await tx.done;
  }

  async clear(): Promise<void> {
    const db = await this.db;
    await db.clear("outbox");
    await db.clear("kv");
  }
}

/** Хранилище в памяти — для тестов и браузеров без IndexedDB. */
export class MemorySyncStorage implements SyncStorage {
  state: CachedState | null = null;
  outbox: MutationEnvelope[] = [];
  async loadState() {
    return this.state;
  }
  async saveState(state: CachedState) {
    this.state = state;
  }
  async loadOutbox() {
    return [...this.outbox];
  }
  async enqueue(env: MutationEnvelope) {
    this.outbox.push(env);
  }
  async remove(ids: string[]) {
    this.outbox = this.outbox.filter((e) => !ids.includes(e.mutationId));
  }
  async clear() {
    this.state = null;
    this.outbox = [];
  }
}
